import { useSuspenseQuery as useTanstackSuspenseQuery, useQuery as useTanstackQuery } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { Boundary } from '~/componentsV2'
import { BotIcon } from '~/componentsV2/BotIcon'
import { StyledLink } from '~/elementsv2'
import { useIsAuthorized } from '~/hooks'
import { listAllIssuesQueryOptions } from '~/queries'
import { getCdmStudioUrl } from '~/shared'
import { trackEvent } from '../../../providers'
import { getQueryOptions, useQuery } from '../../../services'
import { cn, hashIdToNumber } from '../../../utils'
import { IconButton } from '@bpinternal/ui-kit'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@bpinternal/ui-kit-next'
import { AlwaysAliveBadge } from './AlwaysAliveBadge'
import { IntegrationCountBadge } from './IntegrationCountBadge'
import { IssuesBadge } from './IssuesBadge'
import { AnalyticsWidget } from './AnalyticsWidget'
import { BotCardDropdown } from './BotCardDropdown'
import { Edit, EllipsisVertical } from 'lucide-react'

type BotPreviewProps = {
  id: string
  name?: string
  workspaceId: string
  userId: string
  showChart?: boolean
  location?: 'recent_bots' | 'all_bots'
  className?: string
}
export const BotPreviewCard = ({
  id: botId,
  workspaceId,
  name,
  userId,
  location,
  className,
  showChart = true,
}: BotPreviewProps) => {
  const workspace = useTanstackSuspenseQuery(getQueryOptions('workspaces_/$workspaceId_', { workspaceId })).data
  const authorize = useIsAuthorized({ userId, workspaceId: workspace.id })
  const { data: bot } = useQuery('workspaces_/$workspaceId_/bots_/$botId_', { botId, workspaceId: workspace.id })

  const isAuthorizedToUpdateBot = authorize('bots.update')

  const [alwaysAlivePending, setAlwaysAlivePending] = useState(false)

  const integrations = Object.entries(bot?.integrations ?? {}).map(([botIntegrationId, integration]) => ({
    botIntegrationId,
    ...integration,
  }))
  const issues = useTanstackQuery(listAllIssuesQueryOptions({ botId, workspaceId: workspace.id })).data

  const colorIndex = hashIdToNumber(botId, 3)

  const openInStudio = () => {
    window.open(getCdmStudioUrl(botId))
  }

  return (
    <Card className={cn('group flex flex-col justify-between @container', className)}>
      <CardHeader className="p-4">
        <CardTitle className="flex items-center gap-2">
          <BotIcon size="3" id={botId} className="flex-none" />
          <StyledLink
            className="mr-auto font-medium group-hover:truncate"
            to="/workspaces/$workspaceId/bots/$botId"
            params={{ workspaceId: workspace.id, botId }}
            onClick={() => trackEvent({ type: 'navigate_to_bot', from: location })}
          >
            {name ?? bot?.name}
          </StyledLink>
          <IconButton size={'1'} icon={Edit} onClick={openInStudio} variant="ghost" />
          {isAuthorizedToUpdateBot && (
            <BotCardDropdown
              userId={userId}
              botId={botId}
              botName={name ?? bot?.name}
              workspaceId={workspace.id}
              setAlwaysAlivePending={setAlwaysAlivePending}
            >
              <IconButton size={'1'} variant="minimal" color="gray" icon={EllipsisVertical} />
            </BotCardDropdown>
          )}
        </CardTitle>
      </CardHeader>
      {showChart && (
        <Boundary className="ml-auto" suspenseFallback={null} onError={() => null}>
          <CardContent className="p-4 px-2 pt-0">
            <AnalyticsWidget color={(colorIndex + 1) as any} botId={botId} workspaceId={workspace.id} />
          </CardContent>
        </Boundary>
      )}
      <CardFooter className="items-center gap-2 p-4 pt-0">
        <AlwaysAliveBadge alwaysAlive={bot?.alwaysAlive} pending={alwaysAlivePending} />
        <IntegrationCountBadge count={integrations.length} />
        <IssuesBadge botId={botId} issuesCount={issues?.length} />
        <span className="ml-auto text-xs text-gray-10">
          {bot?.deployedAt ? <>Deployed {DateTime.fromISO(bot?.deployedAt).toRelative()}</> : <>Not deployed</>}
        </span>
      </CardFooter>
    </Card>
  )
}
